'use client';

import { API_ROUTES, useApiMutation } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '@shared/components/shadcn';
import { ChatTyping, ChevronLeft, Email, Football, Phone, Slots, TicketStar } from '@shared/icons';
import { ROUTES } from '@shared/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChannelPreferences } from '../../../ChannelPreferences/index';
import { MultistepFormProvider } from '../../../form-controls/MultistepForm/context/MultistepFormContext';
import type { MultistepFormContextType } from '../../../form-controls/MultistepForm/types';
import type { ToggleOption } from '../../../form-controls/ToggleGroup/ToggleGroup';
import { SkipConfirmationDialog } from '../../../Forms/Registration/components/SkipConfirmationDialog/SkipConfirmationDialog';
import { ProductPreferences } from '../../../ProductPreferences/index';
import type { PopupValueResponse } from './types';

const PRODUCT_OPTIONS: ToggleOption[] = [
  { value: 'lottery', label: '', icon: <TicketStar />, trackingProps: { 'data-track-name': 'Lottery' } },
  { value: 'casino', label: '', icon: <Slots />, trackingProps: { 'data-track-name': 'Casino and Scratchcards' } },
  { value: 'sportsbook', label: '', icon: <Football />, trackingProps: { 'data-track-name': 'Sportsbook' } },
];

const CHANNEL_OPTIONS: ToggleOption[] = [
  { value: 'email', label: '', icon: <Email />, trackingProps: { 'data-track-name': 'Email' } },
  { value: 'sms', label: '', icon: <ChatTyping />, trackingProps: { 'data-track-name': 'SMS' } },
  { value: 'phone', label: '', icon: <Phone />, trackingProps: { 'data-track-name': 'Phone' } },
];

type ContactPreferencesPayload = {
  verticals: {
    lottery: boolean;
    casinoAndScratchcards: boolean;
    sportsbook: boolean;
    bingo: boolean;
  };
  notificationChannels: {
    email: boolean;
    sms: boolean;
    phone: boolean;
    post: boolean;
  };
};

function buildContinueButtonTrackingProps(
  productPreferences: string[],
  channelPreferences: string[],
  { selectAllProducts = false, selectAllChannels = false }: { selectAllProducts?: boolean; selectAllChannels?: boolean } = {}
): Record<string, string> {
  const products = selectAllProducts ? PRODUCT_OPTIONS.map(opt => opt.value) : productPreferences;
  const channels = selectAllChannels ? CHANNEL_OPTIONS.map(opt => opt.value) : channelPreferences;

  return {
    'data-track-communication-email': channels.includes('email') ? 'Yes' : 'No',
    'data-track-communication-sms': channels.includes('sms') ? 'Yes' : 'No',
    'data-track-communication-mobile': channels.includes('phone') ? 'Yes' : 'No',
    'data-track-product-lottery': products.includes('lottery') ? 'Yes' : 'No',
    'data-track-product-casino': products.includes('casino') ? 'Yes' : 'No',
    'data-track-product-sportsbook': products.includes('sportsbook') ? 'Yes' : 'No',
  };
}

function buildPayload(productPreferences: string[], channelPreferences: string[]): ContactPreferencesPayload {
  const gamblingPrefs = Array.isArray(productPreferences) ? productPreferences : [];
  const communicationPrefs = Array.isArray(channelPreferences) ? channelPreferences : [];

  return {
    verticals: {
      lottery: gamblingPrefs.includes('lottery'),
      casinoAndScratchcards: gamblingPrefs.includes('casino'),
      sportsbook: gamblingPrefs.includes('sportsbook'),
      bingo: false,
    },
    notificationChannels: {
      email: communicationPrefs.includes('email'),
      sms: communicationPrefs.includes('sms'),
      phone: communicationPrefs.includes('phone'),
      post: false,
    },
  };
}

function ContactPreferencesDialog() {
  const { session } = useSession();
  const isLoggedIn = session.status === 'authenticated';
  const router = useRouter();
  const queryClient = useQueryClient();
  const hasOpenedThisSessionRef = useRef(false);
  const hasFetchedPopupValueRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [productPreferences, setProductPreferences] = useState<string[]>([]);
  const [channelPreferences, setChannelPreferences] = useState<string[]>([]);
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const stepProducts = useTranslations('global_sheets.contact_preferences.product_preferences');
  const stepChannels = useTranslations('global_sheets.contact_preferences.channel_preferences');
  const actions = useTranslations('global_sheets.contact_preferences.actions');
  const tCommon = useTranslations('common');

  const popupValuePath = API_ROUTES.MY_ACCOUNT.CONTACT_PREFERENCES.POPUP_VALUE;
  const { mutate } = useApiMutation<PopupValueResponse, void>(popupValuePath, 'POST', {
    requestOptions: { credentials: true },
    mutationOptions: {
      onSuccess: data => {
        if (data?.popUpValue === 'POP_UP_NOT_CONFIRMED_YET' && !hasOpenedThisSessionRef.current) {
          hasOpenedThisSessionRef.current = true;
          setIsOpen(true);
        }
      },
      onError: () => {
        hasFetchedPopupValueRef.current = false;
      },
    },
  });

  useEffect(() => {
    if (!isLoggedIn) {
      setIsOpen(false);
      hasOpenedThisSessionRef.current = false;
      hasFetchedPopupValueRef.current = false;
      return;
    }
    if (hasFetchedPopupValueRef.current) return;

    hasFetchedPopupValueRef.current = true;
    mutate(undefined);
  }, [isLoggedIn, mutate]);

  const redirectToHomepage = useCallback(() => {
    setIsOpen(false);
    router.push(ROUTES.HOME);
  }, [router]);

  const optOutAllPath = API_ROUTES.MY_ACCOUNT.CONTACT_PREFERENCES.OPT_OUT_ALL;
  const { mutateAsync: optOutAll, isPending: isOptingOut } = useApiMutation<void, void>(optOutAllPath, 'PATCH', {
    requestOptions: { credentials: true },
    mutationOptions: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['api', API_ROUTES.MY_ACCOUNT.CONTACT_PREFERENCES.GET] });
      },
    },
  });

  const { mutateAsync: submitPreferences, isPending: isSubmitting } = useApiMutation<void, ContactPreferencesPayload>(API_ROUTES.MY_ACCOUNT.CONTACT_PREFERENCES.PUT, 'PUT', {
    requestOptions: {
      credentials: true,
      headers: { Accept: 'application/json' },
    },
    mutationOptions: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['api', API_ROUTES.MY_ACCOUNT.CONTACT_PREFERENCES.GET] });
      },
    },
  });

  const saveAndRedirect = useCallback(
    async (products: string[], channels: string[]) => {
      setIsLoading(true);
      try {
        await submitPreferences(buildPayload(products, channels));
      } catch {
        void 0;
      } finally {
        setIsLoading(false);
      }
      redirectToHomepage();
    },
    [submitPreferences, redirectToHomepage]
  );

  const multistepContextValue = useMemo<MultistepFormContextType>(
    () => ({
      currentStepIndex,
      steps: [],
      isFirstStep: currentStepIndex === 0,
      isLastStep: currentStepIndex === 1,
      goToNextStep: async () => setCurrentStepIndex(1),
      goToPreviousStep: () => setCurrentStepIndex(0),
      goToStep: (index: number) => setCurrentStepIndex(index),
      skipToStep: (stepId: string) => {
        if (stepId === 'channel-preferences') setCurrentStepIndex(1);
      },
      isLoading: isLoading || isSubmitting || isOptingOut,
      setIsLoading,
      formData: { productPreferences, channelPreferences },
      updateFormData: () => {},
      setStatus: () => {},
      status: '',
      formErrors: false,
    }),
    [currentStepIndex, productPreferences, channelPreferences, isLoading, isSubmitting, isOptingOut]
  );

  const productOptions = useMemo(
    () =>
      PRODUCT_OPTIONS.map(opt => ({
        ...opt,
        label: stepProducts(opt.value === 'lottery' ? 'lottery' : opt.value === 'casino' ? 'casino-and-scratchcards' : 'sportsbook'),
      })),
    [stepProducts]
  );

  const channelOptions = useMemo(
    () =>
      CHANNEL_OPTIONS.map(opt => ({
        ...opt,
        label: stepChannels(opt.value === 'email' ? 'email' : opt.value === 'sms' ? 'sms' : 'phonecall'),
      })),
    [stepChannels]
  );

  const handleProductContinue = () => {
    if (productPreferences.length === 0) {
      const allProductValues = PRODUCT_OPTIONS.map(opt => opt.value);
      setProductPreferences(allProductValues);
    }
    setCurrentStepIndex(1);
  };

  const handleProductNoOffers = () => {
    setShowSkipDialog(true);
  };

  const handleChannelContinue = async () => {
    const productsToSave = productPreferences.length === 0 ? PRODUCT_OPTIONS.map(opt => opt.value) : productPreferences;
    const channelsToSave = channelPreferences.length === 0 ? CHANNEL_OPTIONS.map(opt => opt.value) : channelPreferences;
    await saveAndRedirect(productsToSave, channelsToSave);
  };

  const handleChannelSkipOffers = () => {
    setShowSkipDialog(true);
  };

  const handleConfirmSkip = async () => {
    setShowSkipDialog(false);
    setIsLoading(true);
    try {
      await optOutAll(undefined);
    } catch {
      void 0;
    } finally {
      setIsLoading(false);
    }
    redirectToHomepage();
  };

  const handleCancelSkip = () => {
    setShowSkipDialog(false);
  };

  const handleBackClick = () => {
    setCurrentStepIndex(0);
  };

  const productPrimaryButtonText = productPreferences.length === 0 ? stepProducts('selectAllAndContinue') : stepProducts('continueWithSelection');
  const channelPrimaryButtonText = channelPreferences.length === 0 ? stepChannels('selectAllAndContinue') : stepChannels('continueWithSelection');

  const productsForTracking = productPreferences.length === 0 ? PRODUCT_OPTIONS.map(opt => opt.value) : productPreferences;
  const channelContinueButtonTrackingProps = buildContinueButtonTrackingProps(productsForTracking, channelPreferences, {
    selectAllChannels: channelPreferences.length === 0,
  });

  if (!isLoggedIn) return null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent
        data-testid="contact-preferences-dialog"
        className="min-h-0"
        onInteractOutside={e => e.preventDefault()}
        onEscapeKeyDown={e => e.preventDefault()}
        aria-labelledby="contact-preferences-dialog-title"
      >
        <MultistepFormProvider value={multistepContextValue}>
          {/* basis-auto + min-h-0 flex chain matches ReKycAlertDialog — Safari needs this for scrollable flex height */}
          <div className="flex min-h-0 flex-1 flex-col basis-auto">
            <DialogHeader className="relative flex items-center justify-center">
              {currentStepIndex !== 0 && (
                <Button
                  variant="secondary"
                  onClick={handleBackClick}
                  className="absolute -mt-2 left-5 top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-full p-0"
                  type="button"
                  aria-label={tCommon('back')}
                  data-track-name="Back"
                  data-testid="dialog-back-button"
                >
                  <ChevronLeft size="small" />
                </Button>
              )}
              <DialogTitle className="paragraph-explanatory-strong">{currentStepIndex === 0 ? stepProducts('header') : stepChannels('header')}</DialogTitle>
            </DialogHeader>

            <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto">
              {currentStepIndex === 0 ? (
                <ProductPreferences
                  className="p-0 md:p-0 pt-10 md:pt-6 px-0"
                  value={productPreferences}
                  onChange={setProductPreferences}
                  options={productOptions}
                  onContinue={handleProductContinue}
                  onNoOffers={handleProductNoOffers}
                  continueButtonText={productPrimaryButtonText}
                  noOffersButtonText={actions('dontSend')}
                  continueButtonTrackName={productPreferences.length === 0 ? 'All Options' : 'Selected Options'}
                  fieldId="marketing-dialog-product-preferences-error"
                  stepLayout={{
                    title: stepProducts('title'),
                    'data-testid': 'contact-preferences-dialog-products-step',
                    showLogo: true,
                    showBackButton: false,
                    dataTrackFeature: 'Log In',
                    dataTrackContainer: 'Products',
                  }}
                  addFooterElement={true}
                />
              ) : (
                <ChannelPreferences
                  className="p-0 md:p-0 pt-10 md:pt-6 px-0"
                  value={channelPreferences}
                  onChange={setChannelPreferences}
                  options={channelOptions}
                  onContinue={handleChannelContinue}
                  onSkipOffers={handleChannelSkipOffers}
                  continueButtonText={channelPrimaryButtonText}
                  skipButtonText={actions('dontSend')}
                  continueButtonTrackName={channelPreferences.length === 0 ? 'All Options' : 'Selected Options'}
                  continueButtonTrackingProps={channelContinueButtonTrackingProps}
                  fieldId="marketing-dialog-channel-preferences-error"
                  fieldTrackHelper="Products"
                  stepLayout={{
                    title: stepChannels('title'),
                    'data-testid': 'contact-preferences-dialog-channels-step',
                    showLogo: true,
                    showBackButton: false,
                    dataTrackFeature: 'Log In',
                    dataTrackContainer: 'Communication Options',
                  }}
                  addFooterElement={true}
                />
              )}
            </div>
          </div>

          <SkipConfirmationDialog
            isOpen={showSkipDialog}
            onConfirm={handleConfirmSkip}
            onCancel={handleCancelSkip}
            dataTrackFeature="Log In"
            dataTrackContainer="Log In Options Confirmation"
          />
        </MultistepFormProvider>
      </DialogContent>
    </Dialog>
  );
}

export { ContactPreferencesDialog };
